import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Grid, Typography, Card, Box, useTheme, Avatar, CircularProgress } from "@mui/material";
// Replacing Lucide icons with standard MUI Icons (use corresponding names/symbols for clarity)
import { AttachMoney, ShoppingCartOutlined, TrendingUp, AccessTime, StoreMallDirectory, Public } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useAuth } from "../../hooks/useAuth";
import { getDashboardData } from "../../hooks/apiHandler";
import OrdersChart from "./comp/OrdersChart";
import SaleCharts from "./comp/SaleCharts";


const MostSellingItemsPieChart = ({ data }) => {
  const theme = useTheme();
  const COLORS = [theme.palette.success.main, theme.palette.primary.main, theme.palette.warning.main, theme.palette.info.main];

  // Use the first 4 items of the data prop for the chart
  const chartData = data.slice(0, 4);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={chartData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={80}
          fill="#8884d8"
          labelLine={false}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend layout="vertical" align="right" verticalAlign="bottom" wrapperStyle={{ paddingLeft: '10px' }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

// --- END MOCKING ---


// Utility to format a date into 'YYYY-MM-DD'
const formatDate = (d) => {
  const date = new Date(d);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Utility to get the start of the week (Sunday)
const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 (Sun) - 6 (Sat)
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

// --- Internal Stat Card Component (Updated for MUI Icons) ---
const StatCard = ({ icon: IconComponent, label, value, unit = '', color }) => {
  const theme = useTheme();

  // Choose color for icon/chip background
  const cardColor = color === 'sales' ? theme.palette.success.main : theme.palette.primary.main;
  const bgColor = color === 'sales' ? theme.palette.success.light : theme.palette.primary.light;

  return (
    <Card elevation={4} sx={{ height: "100%", p: 2, borderRadius: 2, borderLeft: `5px solid ${cardColor}`, transition: '0.3s', '&:hover': { boxShadow: 8 } }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Avatar sx={{ bgcolor: bgColor, color: cardColor }}>
            {/* Render the MUI Icon component */}
            <IconComponent sx={{ fontSize: 24 }} />
          </Avatar>
        </Grid>
        <Grid item xs>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Typography variant="h6" fontWeight="bold">
            {unit}{Number(value).toFixed(unit === '$' ? 2 : 0)}
          </Typography>
        </Grid>
      </Grid>
    </Card>
  );
};


export const Adash = () => {
  const theme = useTheme();
  const [dashData, setDashData] = useState({
    wSale: 0, tSale: 0, oSale: 0, sSale: 0,
    wOrder: 0, tOrder: 0, oOrder: 0, sOrder: 0,
    dailyOrderDetails: [],
    itemCount: [],
    WeeklySales: [],
  });
  const [loading, setLoading] = useState(true);

  // NOTE: Using mock storeID since local storage is inaccessible here
  const storeID = useRef(JSON.parse(window.localStorage.getItem("ds")));
  const { Logout } = useAuth();

  useEffect(() => {
    const fetchDashData = async () => {
      setLoading(true);
      try {
        // Fetching data using the mock getDashboardData
        const resp = await getDashboardData({ storeID: storeID.current }, Logout);

        if (resp.code === 0 && resp.data) {
          const WeeklyData = resp.data.weeklyOrder || [];
          const DailyData = resp.data.todayOrder || [];

          let wSale = 0, wOrder = 0;
          const currentWeek = {};
          const previousWeek = {};

          WeeklyData.forEach((day) => {
            const dayDate = new Date(day.openingTime);
            const key = formatDate(dayDate);
            const now = new Date();
            const diffInDays = Math.floor((now - dayDate) / (1000 * 60 * 60 * 24));

            if (diffInDays < 7) {
              currentWeek[key] = (currentWeek[key] || 0) + (day.totalSales || 0);
            } else if (diffInDays >= 7 && diffInDays < 14) {
              previousWeek[key] = (previousWeek[key] || 0) + (day.totalSales || 0);
            }

            wSale += day.totalSales || 0;
            wOrder += (day.completedOrders || []).length;
          });

          const startOfWeek = getStartOfWeek(new Date());
          const currentWeekDates = [];
          const previousWeekDates = [];

          for (let i = 0; i < 7; i++) {
            const currentDate = new Date(startOfWeek);
            currentDate.setDate(currentDate.getDate() + i);
            currentWeekDates.push(formatDate(currentDate));

            const prevDate = new Date(startOfWeek);
            prevDate.setDate(prevDate.getDate() - 7 + i);
            previousWeekDates.push(formatDate(prevDate));
          }

          const combinedSales = currentWeekDates.map((date, i) => ({
            date,
            current: currentWeek[date] || 0,
            previous: previousWeek[previousWeekDates[i]] || 0,
          }));

          const tSale = DailyData.totalSales || 0;
          const tOrder = (DailyData.completedOrders || []).length;

          let oSale = 0, oOrder = 0;
          let sSale = 0, sOrder = 0;
          const itemCountMap = {};

          DailyData.completedOrders?.forEach((order) => {
            const type = order.type ?? 0;
            const orders = order.orders || [];

            let orderTotal = 0;
            orders.forEach((eachOrder) => {
              eachOrder.order?.forEach((i) => {
                orderTotal += (i.price || 0) * (i.quantity || 0);
                itemCountMap[i.item] = (itemCountMap[i.item] || 0) + (i.quantity || 0);
              });
            });

            if (type === 1) { // Assuming type 1 is online/partner
              oSale += orderTotal;
              oOrder += 1;
            } else if (type === 0) { // Assuming type 0 is in-store
              sSale += orderTotal;
              sOrder += 1;
            }
          });

          setDashData((prev) => ({
            ...prev,
            wSale, wOrder, tSale, tOrder, oSale, oOrder, sSale, sOrder,
            dailyOrderDetails: DailyData.completedOrders || [],
            itemCount: Object.entries(itemCountMap).map(([name, value]) => ({ name, value })),
            WeeklySales: combinedSales,
          }));
        }
      } catch (err) {
        console.error("Dashboard fetch failed:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashData();
  }, []);

  // --- REPLACED LUCIDE ICONS WITH MUI ICONS ---
  const salesMetrics = [
    { icon: AttachMoney, label: "Weekly Sales", value: dashData.wSale, color: 'sales' },
    { icon: AccessTime, label: "Today's Sales", value: dashData.tSale, color: 'sales' },
    { icon: Public, label: "Online Sales", value: dashData.oSale, color: 'sales' },
    { icon: StoreMallDirectory, label: "In-Store Sales", value: dashData.sSale, color: 'sales' },
  ];

  const orderMetrics = [
    { icon: ShoppingCartOutlined, label: "Weekly Orders", value: dashData.wOrder, color: 'primary' },
    { icon: TrendingUp, label: "Today's Orders", value: dashData.tOrder, color: 'primary' },
    { icon: Public, label: "Online Orders", value: dashData.oOrder, color: 'primary' },
    { icon: StoreMallDirectory, label: "In-Store Orders", value: dashData.sOrder, color: 'primary' },
  ];

  if (loading) {
    return (
      <Container sx={{ py: 5, textAlign: 'center' }}>
        <Box>
          <CircularProgress color="primary" sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Loading Dashboard Data...</Typography>
          <Typography variant="body2" color="text.secondary">Fetching sales and order metrics.</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" color={theme.palette.primary.dark} gutterBottom sx={{ mb: 4 }}>
        Sales and Activity Dashboard
      </Typography>

      <Grid container spacing={4}>

        {/* === SALES OVERVIEW CARDS === */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", p: 3, borderRadius: 3, boxShadow: 3, background: theme.palette.success.light + '20' }}>
            <Typography variant="h6" fontWeight="bold" color={theme.palette.success.dark} gutterBottom sx={{ borderBottom: `2px solid ${theme.palette.success.light}`, pb: 1 }}>
              Sales Overview (Total: ${Number(dashData.wSale).toFixed(2)})
            </Typography>
            <Grid container spacing={3} sx={{ pt: 2 }}>
              {salesMetrics.map((item, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <StatCard {...item} unit={item.label.includes('Sales') ? '$' : ''} />
                </Grid>
              ))}
            </Grid>
          </Card>
        </Grid>

        {/* === ORDERS OVERVIEW CARDS === */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", p: 3, borderRadius: 3, boxShadow: 3, background: theme.palette.primary.light + '20' }}>
            <Typography variant="h6" fontWeight="bold" color={theme.palette.primary.dark} gutterBottom sx={{ borderBottom: `2px solid ${theme.palette.primary.light}`, pb: 1 }}>
              Orders Overview (Total: {dashData.wOrder})
            </Typography>
            <Grid container spacing={3} sx={{ pt: 2 }}>
              {orderMetrics.map((item, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <StatCard {...item} />
                </Grid>
              ))}
            </Grid>
          </Card>
        </Grid>

        {/* === CHARTS SECTION === */}

        {/* Weekly Sales Chart */}
        <Grid item xs={12} md={8}>
          <Card elevation={3} sx={{ height: "100%", p: 3, borderRadius: 2, transition: '0.3s' }}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Weekly Sales Comparison
            </Typography>
            {/* <SaleCharts chartData={dashData.WeeklySales} /> */}
            <OrdersChart chartData={dashData.WeeklySales} />
          </Card>
        </Grid>

        {/* Most Selling Items Pie Chart */}
        <Grid item xs={12} md={4}>
          <Card elevation={3} sx={{ height: "100%", p: 3, borderRadius: 2 }}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Top Selling Items
            </Typography>
            <MostSellingItemsPieChart data={dashData.itemCount} />
          </Card>
        </Grid>

        {/* Hourly Sales Chart (Placeholder) */}
        <Grid item xs={12} md={8}>
          <Card elevation={3} sx={{ height: "100%", p: 3, borderRadius: 2 }}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Today's Detailed Order Flow
            </Typography>
            <SaleCharts chartData={dashData.dailyOrderDetails} />
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};
